// src/pages/NewsDetail.js
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getArticles } from "../firebase";

const NewsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const data = await getArticles();
      setAllArticles(data);
      const found = data.find(art => art.slug === slug);
      setArticle(found || null);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get related articles (same category)
  const relatedArticles = allArticles
    .filter(art => art.category === article?.category && art.slug !== slug)
    .slice(0, 3);

  // Add JSON-LD schema for SEO
  useEffect(() => {
    if (article) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": article.title,
        "image": [article.image],
        "datePublished": article.date,
        "dateModified": article.date,
        "author": [{
          "@type": "Person",
          "name": article.author,
          "url": "https://kohlanewspakistan.asia"
        }],
        "publisher": {
          "@type": "Organization",
          "name": "Kohla News",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kohlanewspakistan.asia/logo192.png"
          }
        },
        "description": article.excerpt,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://kohlanewspakistan.asia/news/${article.slug}`
        }
      });
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [article]);

  if (loading) {
    return (
      <div className="py-20 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!article) {
    navigate("/news");
    return null;
  }

  return (
    <div className="py-20 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <Link to="/news" className="inline-flex items-center text-blue-900 hover:text-yellow-500 transition mb-6">
          ← Back to All News
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Main Article */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Featured Image */}
              <div className="relative h-80 bg-gray-300">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/800x400/1a237e/ffffff?text=Kohla+News";
                  }}
                />
                {article.isBreaking && (
                  <span className="absolute top-4 left-4 bg-red-600 text-white px-4 py-1 rounded-full text-sm font-bold animate-pulse">
                    🔴 BREAKING
                  </span>
                )}
              </div>

              {/* Article Content */}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                    {article.category}
                  </span>
                  <span>• {article.date}</span>
                  <span>• {article.readTime}</span>
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-6 border-b border-gray-200 pb-4">
                  <span>By {article.author}</span>
                  <span>|</span>
                  <span>👁️ {article.views || 0} views</span>
                </div>

                <div 
                  className="prose prose-lg max-w-none prose-headings:text-blue-900 prose-a:text-blue-600 hover:prose-a:text-yellow-500"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Tags:</h3>
                    <div className="flex flex-wrap gap-2">
                      {article.tags.map(tag => (
                        <span key={tag} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-4">Related News</h3>
                <div className="space-y-4">
                  {relatedArticles.map(related => (
                    <Link
                      key={related.id}
                      to={`/news/${related.slug}`}
                      className="block hover:bg-gray-50 p-3 rounded-lg transition"
                    >
                      <h4 className="font-semibold text-blue-900 hover:text-yellow-500 transition text-sm">
                        {related.title}
                      </h4>
                      <p className="text-gray-500 text-xs mt-1">{related.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* WhatsApp Follow */}
            <div className="bg-green-50 rounded-xl shadow-lg p-6 mt-6 border border-green-200">
              <h3 className="text-xl font-bold text-green-700 mb-2">Get News on WhatsApp</h3>
              <p className="text-gray-600 text-sm mb-4">
                Join our WhatsApp group for instant breaking news alerts!
              </p>
              <a
                href="https://wa.me/923000000000"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-green-500 hover:bg-green-600 text-white text-center px-4 py-3 rounded-lg font-semibold transition"
              >
                📱 Join Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;